// AI 审单 - 前端 mock adapter
// 目标：让 STAGE=local 下不依赖后端即可完整联调"AI 审单"功能
//
// 做法：
// 1. `installAiAuditMock(axiosInstance)` 在 services/index.ts 启动时调用
// 2. axios request 拦截器劫持 3 个写接口，设置 config.adapter 返回内存 mock：
//    - POST /eps/factory/order/ai-audit/review
//    - POST /eps/factory/order/ai-audit/retry
//    - POST /eps/factory/order/ai-audit/feedback
// 3. `enrichWithAiAudit(model, detail?)` 被 services/factories.ts 调用，给列表/详情
//    返回的 model 挂 aiAudit 字段（仅 orderId 命中 MOCK_SAMPLES 的工单生效）
// 4. 非 STAGE='local' 时所有函数 early return，生产/预发 0 侵入
//
// 运行时构造状态：window.__AI_AUDIT_MOCK__ 挂 getAll / get / setStatus / inject
// 详细使用见 components/AiAuditPanel/README.md

import { AiAuditVO, AI_AUDIT_STATUS } from '@/components/AiAuditPanel/types'
import { MOCK_SAMPLES, AiAuditMockSample } from '@/components/AiAuditPanel/mockFixtures'

// STAGE 为 webpack DefinePlugin 注入的全局，这里统一处理避免 typeof 判断
declare const STAGE: string

// 内存中的"审单结果主表"
interface MockAuditRow extends AiAuditVO {
  orderId: number
}

const store = new Map<number, MockAuditRow>()
// 位置分配：未绑定 fixture 的真实 orderId → 顺序派一个 fixture
// 因为 mock 里的 orderId 是 prod 的，daily 列表里压根没这些 id
// 解决：不要求 id 匹配，而是把 fixture 轮派给 daily 列表里的前 N 条
const usedFixtureIdx = { current: 0 }

function buildVO(sample: AiAuditMockSample, realOrderId: number): MockAuditRow {
  return {
    orderId: realOrderId,
    status: AI_AUDIT_STATUS.SUCCESS,
    reviewed: 0,
    errorCount: sample.error?.length || 0,
    warningCount: sample.warning?.length || 0,
    correct: sample.correct || [],
    warning: sample.warning || [],
    error: sample.error || [],
    feedbacks: [],
  }
}

function loadFixtures() {
  // 启动不预绑定 orderId，等列表滚进来按顺序分配
  // eslint-disable-next-line no-console
  console.info('[AiAuditMock] fixture count:', MOCK_SAMPLES.length,
    '(会按列表顺序分配给前', MOCK_SAMPLES.length, '条工单)')
}

function assignFixtureTo(realOrderId: number): MockAuditRow | null {
  // 已分配过，直接返回
  if (store.has(realOrderId)) return store.get(realOrderId)!
  // 还有 fixture 可派
  if (usedFixtureIdx.current >= MOCK_SAMPLES.length) return null
  const sample = MOCK_SAMPLES[usedFixtureIdx.current++]
  const row = buildVO(sample, realOrderId)
  store.set(realOrderId, row)
  return row
}

function expose() {
  const w: any = typeof window !== 'undefined' ? window : {}
  w.__AI_AUDIT_MOCK__ = {
    getAll: () => Array.from(store.values()),
    get: (id: number) => store.get(id),
    setStatus: (id: number, patch: Partial<MockAuditRow>) => {
      const row = store.get(id)
      if (!row) {
        // eslint-disable-next-line no-console
        console.warn('[AiAuditMock] no row for', id)
        return
      }
      store.set(id, { ...row, ...patch })
      // eslint-disable-next-line no-console
      console.info('[AiAuditMock] updated', id, store.get(id))
    },
    inject: (id: number, vo: MockAuditRow) => store.set(id, vo),
  }
}

// 给列表 / 详情接口的 model 补 aiAudit 字段
// 按"顺序分配"策略：列表前 N 条（N=fixture 数量）各派一个 fixture，
// 一旦分配就固化在 store，后续刷新或详情请求同一 orderId 仍命中同一 fixture
export function enrichWithAiAudit<T extends { id?: number } & any>(model: T, detailMode = false): T {
  if (STAGE !== 'local') return model
  if (!model) return model
  const id = model?.id ?? model?.orderId
  if (id == null) return model
  const row = assignFixtureTo(Number(id))
  if (!row) return model
  if (detailMode) {
    return { ...model, aiAudit: row }
  }
  // 列表态：只带轻量字段
  const {
    status, reviewed, errorCount, warningCount, failedReason, reviewedBy, reviewedByName, reviewedAt,
  } = row
  const light: AiAuditVO = {
    status, reviewed, errorCount, warningCount, failedReason, reviewedBy, reviewedByName, reviewedAt,
  }
  return { ...model, aiAudit: light }
}

// 安装 axios 拦截器
export function installAiAuditMock(axiosInstance: any) {
  if (STAGE !== 'local') return
  loadFixtures()
  expose()

  // 用 request 拦截器把特定 URL 改写为同步返回的 mock，配合 response 拦截器吐结果
  // 技巧：设置 config.adapter 为自定义函数，即可无视 baseURL 返回模拟响应
  axiosInstance.interceptors.request.use(async (config: any) => {
    const url = String(config.url || '')
    const body = config.data ? tryParseJson(config.data) : {}

    if (url.endsWith('/eps/factory/order/ai-audit/review')) {
      config.adapter = makeAdapter(() => {
        const id = Number(body.orderId)
        const row = store.get(id)
        if (!row) return errorResp('该工单无 AI 审单记录')
        if (row.status !== AI_AUDIT_STATUS.SUCCESS) return errorResp('当前状态不可复核')
        store.set(id, {
          ...row,
          reviewed: 1,
          reviewedAt: formatNow(),
          reviewedBy: 99999,
          reviewedByName: 'mock运营',
        })
        return successResp(null)
      })
    }

    if (url.endsWith('/eps/factory/order/ai-audit/retry')) {
      config.adapter = makeAdapter(() => {
        const id = Number(body.orderId)
        const row = store.get(id)
        if (!row) return errorResp('该工单无 AI 审单记录')
        if (row.status !== AI_AUDIT_STATUS.FAILED_PERMANENT) return errorResp('仅失败工单可重审')
        store.set(id, {
          ...row,
          status: AI_AUDIT_STATUS.PROCESSING,
          failedReason: undefined,
        })
        // 模拟 2s 后完成
        setTimeout(() => {
          const latest = store.get(id)
          if (latest) {
            store.set(id, { ...latest, status: AI_AUDIT_STATUS.SUCCESS })
          }
        }, 2000)
        return successResp(null)
      })
    }

    if (url.endsWith('/eps/factory/order/ai-audit/feedback')) {
      config.adapter = makeAdapter(() => {
        const id = Number(body.orderId)
        const row = store.get(id)
        if (!row) return errorResp('该工单无 AI 审单记录')
        const field = body.field
        const type = Number(body.feedbackType) as 1 | 2
        const inW = (row.warning || []).some(w => w.field === field)
        const inE = (row.error || []).some(e => e.field === field)
        if (!inW && !inE) return errorResp('该字段无需反馈')
        const originalLevel = inE ? 2 : 1
        const list = (row.feedbacks || []).filter(fb => fb.field !== field)
        list.push({ field, feedbackType: type, originalAiLevel: originalLevel, operatorId: 99999, operatorName: 'mock运营' })
        store.set(id, { ...row, feedbacks: list })
        return successResp(null)
      })
    }

    return config
  })
}

function tryParseJson(data: any) {
  if (!data) return {}
  if (typeof data === 'object') return data
  try { return JSON.parse(data) } catch { return {} }
}

function makeAdapter(produce: () => any) {
  return (config: any) => {
    return new Promise(resolve => {
      setTimeout(() => {
        const payload = produce()
        resolve({
          data: payload,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          request: {},
        })
      }, 150)
    })
  }
}

function successResp(data: any) {
  return { code: '200', msg: 'ok', data }
}

function errorResp(msg: string) {
  return { code: '500', msg, data: null }
}

function formatNow() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}
